// Angular
import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
// State
import { Selector, State, Action, StateContext, Store } from '@ngxs/store';
import {
	LoadConfigFile,
	SetCookieEnabled,
	SetCurrentModule,
	SetCurrentUrl,
	SetDependenciesVersion,
	SetLogAcknowledge,
	SetReleaseNotes, SetSecretConfigData,
	UpdateAppEnd,
	UpdateAppStart
} from '../action/state.action';
import { LogoutAppUpdate } from '../auth/action/auth.action';
// Model
import { ConfigFileModel } from '../model/config-file.model';
import { AppStateModel } from '../model/app.state.model';
// Service
import { ConfigService } from '../service/config.service';
import { HttpClient } from '@angular/common/http';
// Other
import { map } from 'rxjs/operators';

@State<AppStateModel>({
	name: 'app'
})
@Injectable()
export class AppState {
	constructor(
		private configService: ConfigService,
		private store: Store,
		private http: HttpClient,
		private router: Router
	) {
	}

	/**
	 * Returns INVisionAppState state.
	 * @param state
	 * @constructor
	 */
	@Selector()
	static getAppState(state: AppStateModel): AppStateModel {
		return state;
	}

	/**
	 * Loads the Config File and save to the storage whatever is required
	 * @param ctx
	 */
	@Action(LoadConfigFile)
	async loadConfigFile(ctx: StateContext<AppStateModel>, {appName, preloadedConfigFile}: LoadConfigFile): Promise<any> {
		const state = ctx.getState();

		let configFile: ConfigFileModel = null;

		if (preloadedConfigFile) {
			configFile = preloadedConfigFile;
			this.configService.setApiValuesCallback(preloadedConfigFile);
		} else {
			configFile = await this.configService.getConfig().toPromise();
		}

		let appInfo = {};
		if (state[appName]) {
			appInfo = Object.assign({}, state[appName]);
		}
		appInfo = {
			...appInfo,
			version: configFile.version, //configFile.version,
			mapKey: '',//configFile.publicKey.googleMaps,
			api: configFile.api, // configFile.api
			externalApp: configFile?.external_app || null, // configFile.api
			cookieEnable: configFile?.cookiesConfig?.enabled || false
		};
		ctx.patchState({
			...state,
			[appName]: appInfo
		});
	}

	/**
	 * Finish the Upload process
	 * @param ctx
	 */
	@Action(UpdateAppEnd)
	updateAppEnd(ctx: StateContext<AppStateModel>, { appName }: UpdateAppEnd): void {
		const state = ctx.getState();
		let appInfo = Object.assign({}, state[appName]);
		appInfo = {
			...appInfo,
			update: null
		}
		ctx.patchState({
			...state,
			[appName]: appInfo
		});
	}

	/**
	 * Loads the Config File and save to the storage whatever is required
	 * @param ctx
	 */
	@Action(UpdateAppStart)
	updateAppStart(ctx: StateContext<AppStateModel>, { tagVersion, appName }: UpdateAppStart): void {
		const state = ctx.getState();
		let appInfo = Object.assign({}, state[appName]);
		appInfo.version = {
			...appInfo.version,
			tag: tagVersion
		};
		appInfo = {
			...appInfo,
			update: {
				lastUrl: this.router.url
			},
			logAcknowledge: false
		}
		ctx.patchState({
			...state,
			[appName]: appInfo
		});
		this.store.dispatch(new LogoutAppUpdate());
	}

	/**
	 * Set the Status of the Log Acknowledge
	 * @param ctx
	 * @param logAcknowledge
	 */
	@Action(SetLogAcknowledge)
	setLogAcknowledge(ctx: StateContext<AppStateModel>, { logAcknowledge, appName }: SetLogAcknowledge): void {
		const state = ctx.getState();
		let appInfo = Object.assign({}, state[appName]);
		appInfo = {
			...appInfo,
			logAcknowledge: logAcknowledge
		}
		ctx.patchState({
			...state,
			[appName]: appInfo
		});
	}

	/**
	 * Set the release note at once
	 * @param ctx
	 * @param logAcknowledge
	 */
	@Action(SetReleaseNotes)
	setReleaseNotes(ctx: StateContext<AppStateModel>, { releaseNotes, appName }: SetReleaseNotes): void {
		const state = ctx.getState();
		let appInfo = Object.assign({}, state[appName]);
		appInfo = {
			...appInfo,
			releaseNotes: releaseNotes
		}
		ctx.patchState({
			...state,
			[appName]: appInfo
		});
	}

	/**
	 * Set the dependencies versions
	 * @param ctx
	 * @param logAcknowledge
	 */
	@Action(SetDependenciesVersion)
	SetDependenciesVersion(ctx: StateContext<AppStateModel>, { dependencies, appName }: SetDependenciesVersion): void {
		const state = ctx.getState();
		const appInfo = Object.assign({}, state[appName]);
		appInfo.version.dependencies = dependencies;
		ctx.patchState({
			...state,
			[appName]: appInfo
		});
	}

	/**
	 * Set the current nav url
	 * @param ctx
	 * @param currentUrl
	 */
	@Action(SetCurrentUrl)
	setCurrentUrl(ctx: StateContext<AppStateModel>, { currentUrl, appName }: SetCurrentUrl): void {
		const state = ctx.getState();
		let appInfo = Object.assign({}, state[appName]);
		appInfo = {
			...appInfo,
			currentUrl
		};
		ctx.setState({
			...state,
			[appName]: appInfo
		});
	}

	/**
	  * Set the current module
	  * @param ctx
	  * @param currentModule
	  */
	@Action(SetCurrentModule)
	setCurrentModule(ctx: StateContext<AppStateModel>, { currentModule, appName }: SetCurrentModule): void {
		const state = ctx.getState();
		let appInfo = Object.assign({}, state[appName]);
		appInfo = {
			...appInfo,
			currentModule
		};
		ctx.setState({
			...state,
			[appName]: appInfo
		});
	}

	@Action(SetCookieEnabled)
	setCookieEnabled(ctx: StateContext<AppStateModel>, { enableCookied, appName }: SetCookieEnabled): void {
		const state = ctx.getState();
		let appInfo: any = Object.assign({}, state[appName]);

		appInfo = {
			...appInfo,
			cookieEnable: enableCookied
		};

		ctx.setState({
			...state,
			[appName]: appInfo
		});
	}

	/**
	 * Invoke all configured utils external data like security keys or other that are behind
	 * an authenticated endpoint, not every app has the same requirements
	 * @param ctx
	 * @param appName
	 */
	@Action(SetSecretConfigData)
	async setSecretConfigData(ctx: StateContext<AppStateModel>, {appName, api, route}: SetSecretConfigData): Promise<void> {
		const state = ctx.getState();

		const apiURL = state[appName].api[api] + route;
		const configData = await this.http.get(apiURL + '/uiconfig').pipe(
			map((payload: any) => payload) ).toPromise();

		if (configData ) {
			let appInfo: any = Object.assign({}, state[appName]);

			appInfo = {
				...appInfo,
				configData: configData
			};

			ctx.setState({
				...state,
				[appName]: appInfo
			});
		}
	}
}
